import { flow } from 'fp-ts/function';
import { defaultTo, find, head, isEmpty, reject } from 'lodash/fp';
import { Currency } from '@mews-commander/types';
import { EnumValue } from '@mews/enumify';
import {
    Date,
    DateService,
    DateTime,
    DateTimeLocalizationInfo,
    DateTimeService,
    LocalizedPropertyObject,
    NumberFormatting,
    NumberService,
    NumberServiceOptions,
    Substitutes,
    TimeService,
    type,
    WallClockDate,
    WallClockDateService,
} from '@mews/framework';

export interface AppLocalizationComponentProps {
    getCurrentEnterpriseIanaTimeZoneIdentifier(): string;
    getCurrencyCultureInfo(): NumberFormatting;
    getCurrencyLocalizationInfo(
        currencyCode: string
    ): Pick<Currency, 'symbol' | 'symbolPosition' | 'symbolSpacing' | 'precision'>;
    getCurrentDateTimeLocalizationInfo(): DateTimeLocalizationInfo;
    getDayShortNames(): string[];
    getMonthNames(): string[];
    getMonthShortNames(): string[];
    getNumberCultureInfo(): NumberFormatting;
    getTextLocalizationInfo(): { languageCodes: string[] };
    localizeEnum<Prefix extends keyof Mews.EnumLocalization['prefixes']>(
        prefix: Prefix,
        enumValue: Mews.EnumLocalization['prefixes'][Prefix]['valueType']
    ): string;
    localizeEnumify(enumValue: EnumValue): string;
    localizeText(text: Mews.TextKey, substitutes?: Substitutes): string;
}

export default function AppLocalizationComponent({
    getCurrentEnterpriseIanaTimeZoneIdentifier,
    getCurrencyCultureInfo,
    getCurrencyLocalizationInfo,
    getCurrentDateTimeLocalizationInfo,
    getDayShortNames,
    getMonthNames,
    getMonthShortNames,
    getNumberCultureInfo,
    getTextLocalizationInfo,
    localizeEnum,
    localizeEnumify,
    localizeText,
}: AppLocalizationComponentProps) {
    return type.create(AppLocalizationComponent, {
        /** @deprecated use getCurrentDateTimeLocalizationInfo selector */
        getCurrentDateTimeLocalizationInfo,

        /** @deprecated use LocalizedText or useLocalizeText */
        localizeText,

        /** @deprecated Use localizeEnum instead with TS string enums. Migrate away from enumify enums. See Mews.EnumLocalization['prefixes'] for allowed values. */
        localizeEnumify,

        /** @deprecated use LocalizedEnum or useLocalizeEnum */
        localizeEnum,

        localizeShortDayName: (date: DateTime) => {
            const shortDayNames = getDayShortNames();
            const day = DateTimeService.getDay(date);
            return shortDayNames[day];
        },

        localizeMonthName: (date: DateTime) => {
            const monthNames = getMonthNames();
            const month = DateTimeService.getMonth(date);
            return monthNames[month];
        },

        localizeShortMonthName: (date: DateTime) => {
            const monthNames = getMonthShortNames();
            const month = DateTimeService.getMonth(date);
            return monthNames[month];
        },

        localizeDate: (dateTime: DateTime) => {
            const localizationInfo = getCurrentDateTimeLocalizationInfo();
            const date = DateTimeService.toDate(dateTime);
            return DateService.toString(date, localizationInfo);
        },

        parseDate: (dateString: string) => {
            const localizationInfo = getCurrentDateTimeLocalizationInfo();
            const date = DateService.fromString(
                dateString,
                localizationInfo,
                getCurrentEnterpriseIanaTimeZoneIdentifier()
            );

            return DateTimeService.fromDate(date);
        },
        // dateString with local format, e.g. "15.03.2023"
        parseDateAndShortTime: (dateString: string, shortTimeString: string) => {
            const localizationInfo = getCurrentDateTimeLocalizationInfo();
            const date = DateService.fromString(
                dateString,
                localizationInfo,
                getCurrentEnterpriseIanaTimeZoneIdentifier()
            );
            const time = TimeService.fromShortString(shortTimeString, localizationInfo);
            return DateTimeService.fromDateAndTime(date, time);
        },
        // dateString with a general format 'YYYY-MM-DD'
        parseDateStringAndShortTime: (dateString: string, shortTimeString: string) => {
            const localizationInfo = getCurrentDateTimeLocalizationInfo();
            const date = DateService.parse(dateString, getCurrentEnterpriseIanaTimeZoneIdentifier());
            const time = TimeService.fromShortString(shortTimeString, localizationInfo);
            return DateTimeService.fromDateAndTime(date, time);
        },

        localizeToShortTimeString: (timeString: string) => {
            const localizationInfo = getCurrentDateTimeLocalizationInfo();
            const time = TimeService.parse(timeString);
            return TimeService.toShortString(time, localizationInfo);
        },

        localizeShortTime: (date: DateTime) => {
            const localizationInfo = getCurrentDateTimeLocalizationInfo();
            const time = DateTimeService.toTime(date);
            return TimeService.toShortString(time, localizationInfo);
        },

        parseShortTime: (shortTimeString: string) => {
            const localizationInfo = getCurrentDateTimeLocalizationInfo();
            return TimeService.fromShortString(
                shortTimeString,
                localizationInfo,
                getCurrentEnterpriseIanaTimeZoneIdentifier()
            );
        },

        localizeTime: (date: DateTime) => {
            const localizationInfo = getCurrentDateTimeLocalizationInfo();
            const time = DateTimeService.toTime(date);
            return TimeService.toString(time, localizationInfo);
        },

        localizeDateToString: (date: Date) => {
            const localizationInfo = getCurrentDateTimeLocalizationInfo();
            return DateService.toString(date, localizationInfo);
        },

        localizeShortDate: (date: Date) => {
            const localizationInfo = getCurrentDateTimeLocalizationInfo();
            return DateService.toShortString(date, localizationInfo);
        },

        localizeMonthDate: (date: Date) => {
            const monthNames = getMonthNames();
            const monthName = monthNames[DateService.getMonth(date)];
            const year = date?.internal?.year || '';
            return `${monthName} ${year}`;
        },

        localizeDateTime: (date: DateTime) => {
            const localizationInfo = getCurrentDateTimeLocalizationInfo();
            const dateTime = DateTimeService.toString(date, localizationInfo);
            return dateTime;
        },
        localizeShortDateTime: (dateTime: DateTime) => {
            const localizationInfo = getCurrentDateTimeLocalizationInfo();
            const date = DateTimeService.toDate(dateTime);
            const shortDate = DateService.toShortString(date, localizationInfo);
            const time = DateTimeService.toTime(dateTime);
            const shortTime = TimeService.toShortString(time, localizationInfo);
            return `${shortDate} ${shortTime}`;
        },

        localizeProperty: (property: LocalizedPropertyObject | null | undefined) => {
            if (!property) {
                return '';
            }

            const { languageCodes } = getTextLocalizationInfo();
            const languageCode = find((lCode: string) => !!property[lCode])(languageCodes);

            if (languageCode) {
                return property[languageCode];
            }

            return flow(reject(isEmpty), head, defaultTo(''))(property);
        },

        localizeNumber: (number: number, options: NumberServiceOptions = {}) => {
            const cultureInfo = getNumberCultureInfo();
            return NumberService.format(number, cultureInfo, options);
        },

        parseNumber: (number: string) => {
            const cultureInfo = getNumberCultureInfo();
            return NumberService.unformat(number, cultureInfo);
        },

        parseCurrencyValue: (number: string) => {
            const cultureInfo = getCurrencyCultureInfo();
            return NumberService.unformat(number, cultureInfo);
        },

        localizePercent: (number: number, options: NumberServiceOptions = {}) => {
            const cultureInfo = getNumberCultureInfo();
            return NumberService.formatPercent(number, cultureInfo, options);
        },

        parsePercent: (number: string) => {
            const cultureInfo = getNumberCultureInfo();
            return NumberService.unformatPercent(number, cultureInfo);
        },

        localizeCurrencyValue: (
            { value, currency }: { value: number; currency: string },
            options: NumberServiceOptions = {}
        ) => {
            const currencyLocalizationInfo = getCurrencyLocalizationInfo(currency);
            const currencyCultureInfo = getCurrencyCultureInfo();
            const localizationInfo = {
                ...currencyLocalizationInfo,
                ...currencyCultureInfo,
            };

            return NumberService.formatCurrencyValue(value, localizationInfo, options);
        },

        parseWallClockDate: (dateString: string) => {
            const localizationInfo = getCurrentDateTimeLocalizationInfo();
            return WallClockDateService.fromString(dateString, localizationInfo);
        },

        localizeWallClockDate: (wallClockDate: WallClockDate) => {
            const localizationInfo = getCurrentDateTimeLocalizationInfo();
            return WallClockDateService.toString(wallClockDate, localizationInfo);
        },
    });
}
